import { ApiTarget, type HttpClient } from '@packages/core/http';
import { Constants } from '@packages/core/config';
import { getLogger } from '@packages/core/logging';
import { type Account, type AccountResponseDto } from '@packages/models/api';

import {
    type AccountDocsType,
    type ApplicableVehicle,
    type DocumentVersion,
    type PreviewHeaderResponse,
    type PublicationsDto,
    type PublicationType,
    type QuickReferenceResponse,
    type ServiceDiagnosticsBundlesDto,
    type ServiceDiagnosticsResponse,
    type ServiceDiagnosticsSectionsDto,
    type TroubleshootingSearchRequest,
    type TroubleshootingSearchResponse,
    type VehicleCarline,
} from '../types';
import { type TransferProgress, isSuccessStatus, sendWithProgress } from '../utils/upload-transfer';

const logger = getLogger('documents');

export class DocsService {
    constructor(private http: HttpClient) {}

    getAccountFavorites({
        body,
        params,
        ignoreCache,
        signal,
    }: {
        body: {
            docStatus: string;
            modelYear?: number;
            carlineId?: string;
            searchType: string;
        };
        params: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = '/docs/search-docs' + (params.length > 0 ? `${params}` : '');

        return this.http.orchestrateRequest<PublicationsDto>({
            method: 'PUT',
            url,
            body,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getWhatsNew({ ignoreCache, signal }: { ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = `/docs/all-docs?page=0&size=10`;

        return this.http.orchestrateRequest<PublicationsDto>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getQuickReferences({
        queryParameters,
        ignoreCache = true,
        signal,
    }: {
        queryParameters: { publicationType: string; carlineId: string; modelYear: number; page: number; size: number };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<QuickReferenceResponse>({
            method: 'GET',
            url: `/docs/quick-references${this.http.queryString(queryParameters)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getDocumentTypes({ ignoreCache, signal }: { ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = '/docs/document-types';

        return this.http.orchestrateRequest<PublicationType[]>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getDocumentCodes({ ignoreCache, signal }: { ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = '/docs/document-codes';

        return this.http.orchestrateRequest<{
            types: string[];
            carlines: string[];
            codes: {
                code: string;
                types: number[];
                carlines: number[];
                modelYears: number[];
                allVehicles: boolean;
            }[];
        }>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getDocumentPreview({
        queryParameters,
        account,
        ignoreCache,
        signal,
    }: {
        queryParameters: { query: string; docId: string; page: number; size: number };
        account: Account | null;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<PreviewHeaderResponse>({
            method: 'GET',
            url: `/${account ? 'docs' : 'public'}/page-headline${this.http.queryString(queryParameters)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getDocumentUrl({
        queryParameters,
        ignoreCache,
        signal,
    }: {
        queryParameters: { docId: string; activityType?: string };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<string>({
            method: 'GET',
            url: `/docs/view-doc${this.http.queryString(queryParameters)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
            responseType: 'text',
        });
    }

    getPublicDocumentUrl({
        queryParameters,
        ignoreCache,
        signal,
    }: {
        queryParameters: { docId: string; activityType?: string };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<string>({
            method: 'GET',
            url: `/public/view-doc${this.http.queryString(queryParameters)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
            responseType: 'text',
        });
    }

    dummyPostOperation({
        body,
        ignoreCache,
        signal,
    }: {
        body?: { documentId: string; documentPageId: string; pageNumber: number };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<string>({
            method: 'POST',
            url: `/docs/document-page-preview`,
            body,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getAccountDocs({
        queryParameters,
        ignoreCache = true,
        signal,
    }: {
        queryParameters: {
            type: AccountDocsType;
            carlineId?: string;
            modelYear?: number;
            page?: number | string;
            size?: number | string;
            sort?: string;
        };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<PublicationsDto>({
            method: 'GET',
            url: `/docs/account-docs${this.http.queryString(queryParameters)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    removeDocumentsFromRecents({ docIds, signal }: { docIds: string[]; signal?: AbortSignal }) {
        // Built by hand because the parameter repeats, and queryString writes each key once.
        const query = docIds.map((docId) => `docIds=${encodeURIComponent(docId)}`).join('&');

        return this.http.orchestrateRequest<void>({
            method: 'DELETE',
            url: `/docs/recent-docs?${query}`,
            apiTarget: ApiTarget.VinService,
            responseType: 'text',
            signal,
        });
    }

    saveDocumentToRecents({
        docId,
        ignoreCache,
        signal,
    }: {
        docId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<string>({
            method: 'POST',
            url: `/docs/save-recent-doc/${docId}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getServiceManualUrl({
        queryParameters,
        ignoreCache,
        signal,
    }: {
        queryParameters: { docId: string };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<string>({
            method: 'GET',
            url: `/docs/cdn-cookie-url${this.http.queryString(queryParameters)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getRecentDocsSearch({ ignoreCache, signal }: { ignoreCache?: boolean; signal?: AbortSignal }) {
        return this.http.orchestrateRequest<{ recentSearches: string[] }>({
            method: 'GET',
            url: `/docs/recent-search`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    downloadPublication({
        queryParameters,
        ignoreCache,
        signal,
    }: {
        queryParameters: { docId: string };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<string>({
            method: 'GET',
            url: `/docs/download-doc${this.http.queryString(queryParameters)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
            responseType: 'text',
        });
    }

    createPublication({ body, ignoreCache, signal }: { body?: any; ignoreCache?: boolean; signal?: AbortSignal }) {
        // placeholder to update return type
        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url: '/docs/admin/document',
            body,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
            responseType: 'text',
        });
    }

    createVersionDocumentId({
        pubVersion,
        parentDocumentId,
        ignoreCache = true,
    }: {
        pubVersion: number;
        parentDocumentId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        // placeholder to update return type
        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url: '/docs/admin/document/version',
            body: {
                pubVersion,
                parentDocumentId,
            },
            ignoreCache,
            apiTarget: ApiTarget.VinService,
            responseType: 'text',
        });
    }

    editPublication({
        publicationId,
        body,
        ignoreCache,
        signal,
    }: {
        publicationId: string;
        body: any;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        // placeholder to update return type
        return this.http.orchestrateRequest<any>({
            method: 'PUT',
            url: `/docs/admin/document/${publicationId}`,
            body,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
            // Validation errors come back as plain text. With a JSON response type the parse
            // fails and the error reaches callers as success: true with an empty body.
            responseType: 'text',
        });
    }

    fetchPublicationFormById(publicationFormId: string) {
        // placeholder to update return type and url
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/docs/admin/document/${publicationFormId}`,
            apiTarget: ApiTarget.VinService,
        });
    }

    deletePublicationById(publicationFormId: string) {
        return this.http.orchestrateRequest<any>({
            method: 'DELETE',
            url: `/docs/admin/document/${publicationFormId}`,
            apiTarget: ApiTarget.VinService,
            responseType: 'text',
        });
    }

    getDetailsByModalCode(modelCode: string) {
        return this.http.orchestrateRequest<{ modelYear: number; carline: string; carlineId: string }>({
            method: 'GET',
            url: `/docs/vehicle-by-model-code?modelCode=${modelCode}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache: true,
        });
    }

    getDetailsByCarlineID(carlineId: string) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/docs/vehicle/model-years?carlineId=${carlineId}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache: true,
        });
    }

    getPublicDetailsByCarlineID(carlineId: string) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/public/vehicle/model-years?carlineId=${carlineId}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache: true,
        });
    }

    getVehicleModelYears({ signal }: { signal?: AbortSignal } = {}) {
        return this.http.orchestrateRequest<number[]>({
            method: 'GET',
            url: '/docs/vehicle/model-years',
            apiTarget: ApiTarget.VinService,
            signal,
        });
    }

    getCarlinesByModelYear({ modelYear, signal }: { modelYear: string; signal?: AbortSignal }) {
        return this.http.orchestrateRequest<VehicleCarline[]>({
            method: 'GET',
            url: `/docs/vehicle/carline${this.http.queryString({ modelYear })}`,
            apiTarget: ApiTarget.VinService,
            signal,
        });
    }

    getVehiclesByModelYearAndCarline({
        modelYear,
        carlineId,
        signal,
    }: {
        modelYear: string;
        carlineId: string;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<ApplicableVehicle[]>({
            method: 'GET',
            url: `/docs/vehicles${this.http.queryString({ modelYear, vehicleCarlineId: carlineId })}`,
            apiTarget: ApiTarget.VinService,
            signal,
        });
    }

    getDetailsByCarlineIDAndModelYear(carlineId: string, modelYear: string) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/docs/vehicle/model-codes?carlineId=${carlineId}&modelYear=${modelYear}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache: true,
        });
    }

    getPublicDetailsByCarlineIDAndModelYear(carlineId: string, modelYear: string) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/public/vehicle/model-codes?carlineId=${carlineId}&modelYear=${modelYear}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache: true,
        });
    }

    getPublicationVersions(publicationFormId: string) {
        return this.http.orchestrateRequest<DocumentVersion>({
            method: 'GET',
            url: `/docs/admin/document/${publicationFormId}/versions`,
            apiTarget: ApiTarget.VinService,
        });
    }

    getUserInfoCardsAccount(accountId: string) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/userInfoCardsAccount?accountId=${accountId}`,
        });
    }

    getAllServiceDiagnostics() {
        return this.http.orchestrateRequest<ServiceDiagnosticsResponse>({
            method: 'GET',
            url: `/docs/all-service-diagnostics`,
            ignoreCache: true,
            apiTarget: ApiTarget.VinService,
        });
    }

    searchServiceDiagnosticsBundles({
        queryParameters,
        ignoreCache,
        signal,
    }: {
        queryParameters: {
            query?: string;
            searchType?: string;
            carlineId?: string;
            modelYear?: number;
            page?: number;
            size?: number;
            sort?: string;
        };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const { sort, ...rest } = queryParameters;
        const query = this.http.queryString(rest);
        const url = `/docs/service-diagnostics/bundles${query}` + (sort ? `${query ? '&' : '?'}sort=${sort}` : '');

        return this.http.orchestrateRequest<ServiceDiagnosticsBundlesDto>({
            method: 'GET',
            url,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getServiceDiagnosticsPages({
        bundleId,
        queryParameters,
        ignoreCache,
        signal,
    }: {
        bundleId: string;
        queryParameters: { query?: string; page?: number; size?: number };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<ServiceDiagnosticsSectionsDto>({
            method: 'GET',
            url: `/docs/service-diagnostics/bundles/${bundleId}/pages${this.http.queryString(queryParameters)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getServiceEngineers() {
        return this.http.orchestrateRequest<{
            allowedAccounts: { accountId: string; soaUsername: string; firstName: string; lastName: string }[];
        }>({
            method: 'GET',
            url: `/stis/troubleshooting/users`,
            ignoreCache: true,
        });
    }

    searchTroubleshootingUsers(query: string, page = 0) {
        return this.http.orchestrateRequest<{
            allowedAccounts: { accountId: string; soaUsername: string; firstName: string; lastName: string }[];
        }>({
            method: 'GET',
            url: `/stis/troubleshooting/users?page=${page}&query=${encodeURIComponent(query)}`,
            ignoreCache: true,
        });
    }
    getStisCdnCookieUrl(documentId: string) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/docs/cdn-cookie-url?docId=${documentId}`,
            apiTarget: ApiTarget.VinService,
        });
    }

    getSsmRedirectUrl(s3path: string) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/docs/ssm-redirect-url?s3path=${s3path}`,
            apiTarget: ApiTarget.VinService,
        });
    }

    searchTroubleshootingSessions({
        body,
        ignoreCache = true,
        signal,
    }: {
        body: TroubleshootingSearchRequest;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<TroubleshootingSearchResponse>({
            method: 'POST',
            url: '/workflowdata/search',
            body,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getPublicationsByDocStatus({
        uri,
        body,
        ignoreCache = true,
        signal,
    }: {
        uri: string;
        body: any;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = '/docs/search-docs' + (uri.length > 0 ? `${uri}` : '');

        return this.http.orchestrateRequest<PublicationsDto>({
            method: 'PUT',
            url,
            body,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    optInUser({ ignoreCache = true, signal }: { ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = '/accounts/stis-optin';

        return this.http.orchestrateRequest<PublicationsDto>({
            method: 'PATCH',
            url,
            ignoreCache,
            signal,
            responseType: 'text',
        });
    }

    changePreference({
        accountId,
        body,
        ignoreCache = true,
        signal,
    }: {
        accountId: string;
        body: { stisFeedbackPrompt: boolean };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = `/accounts/${accountId}`;

        return this.http.orchestrateRequest<AccountResponseDto>({
            method: 'PATCH',
            url,
            body,
            ignoreCache,
            signal,
        });
    }

    submitFeedback({ body, ignoreCache = true, signal }: { body: any; ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = '/docs/user-feedback';

        return this.http.orchestrateRequest<AccountResponseDto>({
            method: 'POST',
            url,
            body,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getAdminVehicleOptions({ ignoreCache = true, signal }: { ignoreCache?: boolean; signal?: AbortSignal } = {}) {
        return this.http.orchestrateRequest<{ vehicleId: string; description: string }[]>({
            method: 'GET',
            url: '/docs/admin/vehicles',
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    associateVehiclesToPublication({
        publicationFormId,
        body,
        signal,
    }: {
        publicationFormId: string;
        body: any;
        signal?: AbortSignal;
    }) {
        const url = `/docs/admin/document/${publicationFormId}/associate-vehicles`;

        return this.http.orchestrateRequest<any>({
            method: 'PUT',
            url,
            body,
            ignoreCache: true,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    associateDocuments({
        publicationFormId,
        body,
        method,
        ignoreCache = true,
        signal,
    }: {
        publicationFormId: string;
        body: any;
        method: 'POST';
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = `/docs/admin/document/${publicationFormId}/associate-publications`;

        return this.http.orchestrateRequest<any>({
            method,
            url,
            body,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    fetchEmergencyFilters({ ignoreCache = true, signal }: { ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = `/public/filter?documentType=EMERGENCY_RESPONSE`;

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    fetchAllEmergencyGuides({ ignoreCache = true, signal }: { ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = `/public/all-emergency-response-docs`;

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    fetchAssociatedVehicle({
        publicationFormId,
        ignoreCache = true,
        signal,
    }: {
        publicationFormId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/docs/admin/document/${publicationFormId}/associate-vehicles`,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    fetchAssociatedPublications({
        publicationFormId,
        ignoreCache = true,
        signal,
    }: {
        publicationFormId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/docs/admin/document/${publicationFormId}/associate-publications`,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    deleteAssociatedPublication({
        associatedDocId,
        ignoreCache = true,
        signal,
    }: {
        associatedDocId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any>({
            method: 'DELETE',
            url: `/docs/admin/document/associate-publications/${associatedDocId}`,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    deleteAllAssociatedPublication({
        associatedDocId,
        ignoreCache = true,
        signal,
    }: {
        associatedDocId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any>({
            method: 'DELETE',
            url: `/docs/admin/document/${associatedDocId}/associate-publications`,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    uploadDocument({
        body,
        ignoreCache = true,
        signal,
    }: {
        body: {
            fileName: string;
            fileType: string;
            documentId: string | undefined;
            sizeInBytes: number;
            createdAt: string;
            createdBy: string;
        };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url: `/docs/admin/upload-doc`,
            body: body,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getUploadStatus({
        body,
        ignoreCache = true,
        signal,
    }: {
        body: {
            documentId: string | undefined;
        };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url: `/docs/admin/upload-doc`,
            body: body,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    uploadZipDocument({
        documentId,
        file,
        ignoreCache = true,
        signal,
    }: {
        documentId: string;
        file?: File;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        let body: FormData | undefined;
        if (file) {
            body = new FormData();
            body.append('zipFile', file);
        }
        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url: `/docs/admin/upload-zip?documentId=${documentId}`,
            body,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    deleteDocument({
        documentId,
        ignoreCache = true,
        signal,
    }: {
        documentId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any>({
            method: 'DELETE',
            url: `/docs/admin/remove-doc/${documentId}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    async sendZipDocument({
        documentId,
        file,
        onProgress,
        signal,
    }: {
        documentId: string;
        file: File;
        onProgress?: (progress: TransferProgress) => void;
        signal?: AbortSignal;
    }) {
        const token = await this.http.getFreshAccessToken();
        const body = new FormData();
        body.append('zipFile', file);
        return sendWithProgress({
            method: 'POST',
            url: `${this.http.vinUrl}/docs/admin/upload-zip?documentId=${documentId}`,
            body,
            headers: token ? { [Constants.AccessTokenHeaderName]: token } : {},
            onProgress,
            signal,
        });
    }

    async uploadFileToS3({
        file,
        preSignedUploadUrl,
        onProgress,
        signal,
    }: {
        file: File;
        preSignedUploadUrl: string;
        onProgress?: (progress: TransferProgress) => void;
        signal?: AbortSignal;
    }) {
        const response = await sendWithProgress({
            method: 'PUT',
            url: preSignedUploadUrl,
            body: file,
            headers: { 'Content-Type': file.type },
            onProgress,
            signal,
        });
        if (!isSuccessStatus(response.status)) {
            logger.error(`Failed to upload a file to S3 [${response.status}]`, {
                fileSize: file.size,
                fileType: file.type,
            });
            throw new Error(`Failed to upload file to S3 [${response.status}]`);
        }
        return response.body;
    }

    getTags({
        documentId,
        ignoreCache = true,
        signal,
    }: {
        documentId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<
            Array<{
                documentId: string;
                dtcCodes: string[];
                keyWords: string[];
                toolNumbers: string[];
            }>
        >({
            method: 'GET',
            url: `/tags/${documentId}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    saveTags({
        documentId,
        body,
        ignoreCache = true,
        signal,
    }: {
        documentId: string;
        body: {
            dtcCodes: string[];
            keyWords: string[];
            toolNumbers: string[];
        };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<
            Array<{
                documentId: string;
                dtcCodes: string[];
                keyWords: string[];
                toolNumbers: string[];
            }>
        >({
            method: 'POST',
            url: `/tags/${documentId}`,
            body,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    searchTags({
        keyword,
        ignoreCache = true,
        signal,
    }: {
        keyword: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<
            Array<{
                documentId: string;
                dtcCodes: string[];
                keyWords: string[];
                toolNumbers: string[];
            }>
        >({
            method: 'GET',
            url: `/tags/search?keyword=${encodeURIComponent(keyword)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getTagsByType({
        tagType,
        ignoreCache = true,
        signal,
    }: {
        tagType: 'DTC' | 'KEYWORD' | 'TOOL_NUMBER';
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<
            Array<{
                documentId: string;
                dtcCodes: string[];
                keyWords: string[];
                toolNumbers: string[];
            }>
        >({
            method: 'GET',
            url: `/tags/byType/${tagType}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }
}
